import { useRouter } from '@tanstack/react-router'
import { ArrowRightIcon, CreditCardIcon, LogOutIcon } from 'lucide-react'

import { Button, Card, Link } from '@/components'
import {
  TRIAL_LENGTH_DAYS,
  TRIAL_PAGE_GRANT,
  useAccountMutating,
} from '@/modules/account'
import { AddCardSteps } from '../components/AddCardSteps'
import { AlreadyMemberNotice } from '../components/AlreadyMemberNotice'
import { PaymentTrust } from '../components/PaymentTrust'
import { TrialPageFrame } from '../components/TrialPageFrame'
import { useStartTrialCheckout } from '../hooks/useStartTrialCheckout'
import { useAuthMutations, useUser } from '../stores'
import type { TrialSignUpDetails } from '../utils/onboarding'

interface TrialAddCardPageProps {
  details: TrialSignUpDetails
}

export const TrialAddCardPage = ({ details }: TrialAddCardPageProps) => {
  const router = useRouter()
  const { signOut } = useAuthMutations()
  const email = useUser()?.email
  const logOut = () => signOut().then(() => router.navigate({ to: '/login' }))

  return (
    <TrialPageFrame
      headerAction={
        <Button theme="ghost" onClick={logOut}>
          <LogOutIcon />
          Log out
        </Button>
      }
    >
      <main className="flex flex-1 items-center justify-center">
        <AddCardCard details={details} />
      </main>
      <p className="text-center text-[13px] text-muted-foreground">
        Signed in as {email}. Not you?{' '}
        <Link asChild theme="brand" className="font-medium">
          <button type="button" onClick={logOut}>
            Log out
          </button>
        </Link>
      </p>
    </TrialPageFrame>
  )
}

const AddCardCard = ({ details }: TrialAddCardPageProps) => {
  const { startTrialCheckout, isAlreadyMember } = useStartTrialCheckout()
  const isOpeningCheckout = useAccountMutating()

  return (
    <Card className="flex w-full max-w-120 flex-col gap-6 p-6 sm:p-10">
      <span className="flex size-12 items-center justify-center rounded-full bg-brand/10 text-brand-deep">
        <CreditCardIcon aria-hidden className="size-6" />
      </span>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-balance">
          Add a card to start the trial.
        </h1>
        <p className="text-[15px] text-muted-foreground">
          Your trial gives you {TRIAL_LENGTH_DAYS} days and {TRIAL_PAGE_GRANT}{' '}
          pages, with full reconciliation.
        </p>
      </div>
      <AddCardSteps />
      <div className="flex flex-col gap-2.5">
        <Button
          size="lg"
          className="w-full"
          isLoading={isOpeningCheckout}
          loadingText="Opening checkout..."
          onClick={() => startTrialCheckout(details)}
        >
          Add a card
          <ArrowRightIcon />
        </Button>
        <p className="text-center text-[13px] text-muted-foreground">
          You will not be charged during the trial.
        </p>
        {isAlreadyMember && <AlreadyMemberNotice />}
      </div>
      <PaymentTrust />
    </Card>
  )
}
